"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return RotationGroup;
    }
});
const _konva = /*#__PURE__*/ _interop_require_default(require("konva"));
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function degToRad(angle) {
    return angle / 180 * Math.PI;
}
function getRelativeRotation(t0, tf) {
    /**
   * return the offset rotation to move from initial to final angle
   * find minimum offset on unit circle
   */ return (360 + (tf - t0)) % 360;
}
const getCenter = (group)=>{
    const bbox = group.getClientRect();
    return {
        x: bbox.width / 2,
        y: bbox.height / 2
    };
};
function rotatePoint(point, deg) {
    const rad = degToRad(deg || 0);
    const rcos = Math.cos(rad);
    const rsin = Math.sin(rad);
    return {
        x: point.x * rcos - point.y * rsin,
        y: point.x * rsin + point.y * rcos
    };
}
function rotateAroundPoint(point, origin, rotateBy) {
    const dxdy = {
        x: point.x - origin.x,
        y: point.y - origin.y
    };
    // rotation-in-place
    const rotated = rotatePoint(dxdy, rotateBy);
    // followed by a translation (or move)
    return {
        x: origin.x + rotated.x,
        y: origin.y + rotated.y
    };
}
function scale(shape, scaleTo, origin) {
    origin = origin !== null && origin !== void 0 ? origin : {
        x: shape.x,
        y: shape.y
    };
    const scaleInitial = shape.scale;
    const scaleDelta = {
        x: scaleTo.x / scaleInitial.x,
        y: scaleTo.y / scaleInitial.y
    };
    const position = {
        x: shape.x - (origin.x - shape.x) * (scaleDelta.x - 1),
        y: shape.y - (origin.y - shape.y) * (scaleDelta.y - 1)
    };
    return position;
}
/**
 * Scales the given group to fit the stage, while keeping it centered.
 * @param {Konva.Group} group - The group to scale.
 * @param {boolean} center - Whether to keep the group centered after scaling.
 * @returns {Object} - The new position and scale of the group.
 */ function scaleToFit(group, center) {
    // Compute group dimensions and/or override if not provided
    const boundingBox = group.getClientRect();
    const shape = {
        x: group.x(),
        y: group.y(),
        width: group.width() || boundingBox.width,
        height: group.height() || boundingBox.height,
        scale: group.scale(),
        rotation: group.rotation()
    };
    let position = {
        x: shape.x,
        y: shape.y
    };
    let scaleTo = {
        x: 1,
        y: 1
    };
    if (shape.width > 0 && shape.height > 0) {
        // Get stage properties
        const stage = group.getStage();
        const stageWidth = stage.width();
        const stageHeight = stage.height();
        // Compute the equivalent rotated stage to determine best-fit rescale
        const rad = degToRad(shape.rotation);
        const rcos = Math.cos(rad);
        const rsin = Math.sin(rad);
        const rotated = {
            width: Math.abs(stageWidth * rcos) + Math.abs(stageHeight * rsin),
            height: Math.abs(stageWidth * rsin) + Math.abs(stageHeight * rcos)
        };
        // Get best fit
        const scaleToBestFit = Math.min(stageWidth / rotated.width, stageHeight / rotated.height);
        // compute new position from scale about center point
        scaleTo = {
            x: scaleToBestFit,
            y: scaleToBestFit
        };
        position = scale(shape, scaleTo, center);
    }
    return {
        position,
        scale: scaleTo
    };
}
class RotationGroup extends _konva.default.Group {
    setPivotPoint(point) {
        if (typeof point.x === "number" && typeof point.y === "number") {
            this.pivotPoint = point;
        } else {
            console.error("Invalid pivot point coordinates");
        }
    }
    getPivotPoint() {
        var _this_pivotPoint;
        return (_this_pivotPoint = this.pivotPoint) !== null && _this_pivotPoint !== void 0 ? _this_pivotPoint : getCenter(this);
    }
    rotateBy(theta) {
        var // Update canvas group
        _this_getLayer;
        const pivot = this.getPivotPoint();
        const prevRotation = this.rotation();
        const upperLeft = this.position();
        const rotateBy = getRelativeRotation(prevRotation, theta);
        const position = rotateAroundPoint(upperLeft, pivot, rotateBy);
        super.setAttrs({
            position,
            rotation: theta
        });
        // Scale to fit
        const scaledPosition = scaleToFit(this, pivot);
        this.setAttrs({
            position: scaledPosition.position,
            scale: scaledPosition.scale
        });
        (_this_getLayer = this.getLayer()) === null || _this_getLayer === void 0 ? void 0 : _this_getLayer.batchDraw();
    }
    constructor(props = {}){
        const { center } = props;
        super(props);
        this.attrs.name = "RotationGroup";
        this.pivotPoint = center;
        this.initialRotation = 0;
    }
}
